import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Home as HomeIcon, DollarSign, Calculator, Map, Phone, ClipboardCheck } from "lucide-react";
import { MaskLine, Reveal, stagger, item } from "@/components/Motion";
import { COUNTIES, FEATURED_SEARCHES, AGENT, IMAGES } from "@/data";
import { LeadDialog } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";

const JOURNEYS = ["Buy my first home", "Buy another home", "Sell my home", "Buy and sell", "Invest in real estate", "Relocate to Georgia", "Explore my options"];
const journeyLink = (j) => {
  if (j.includes("Sell") && !j.includes("and")) return "/sell";
  if (j.includes("and sell")) return "/contact";
  if (j.includes("Relocate")) return "/areas";
  if (j.includes("Explore")) return "/contact";
  return "/buy";
};

const TOOLS = [
  { title: "Homebuyer Readiness Quiz", desc: "A guided questionnaire that maps your next steps.", icon: ClipboardCheck, to: "/buy/questionnaire" },
  { title: "Mortgage Payment Calculator", desc: "Estimate a monthly payment in seconds.", icon: Calculator, to: "/calculators" },
  { title: "Home Value Request", desc: "Get a local, agent-prepared value review.", icon: DollarSign, to: "/sell" },
  { title: "Georgia Relocation Guide", desc: "Explore Metro Atlanta counties & communities.", icon: Map, to: "/areas" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -160]);

  return (
    <div className="bg-cream">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100vh] flex flex-col justify-center px-6 md:px-12 pt-28 pb-20 overflow-hidden">
        <motion.div style={{ y }} className="absolute right-0 top-24 w-[46%] h-[70%] hidden lg:block">
          <div className="relative w-full h-full">
            <img src={IMAGES.home1} alt="Metro Atlanta home" className="absolute right-0 top-0 w-[80%] h-[70%] object-cover rounded-[2rem] shadow-2xl" />
            <img src={IMAGES.interior2} alt="Home interior" className="absolute left-0 bottom-0 w-[55%] h-[55%] object-cover rounded-[2rem] shadow-2xl border-8 border-cream" />
          </div>
        </motion.div>
        <div className="relative max-w-7xl mx-auto w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-green mb-6">
            Fulton · Gwinnett · DeKalb · Walton
          </motion.p>
          <h1 className="font-serif text-navy text-[15vw] sm:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tighter max-w-4xl">
            <MaskLine delay={0.1}>Your Next</MaskLine>
            <MaskLine delay={0.22}>Move Starts <span className="text-gold italic">Here</span></MaskLine>
          </h1>
          <Reveal delay={0.5} className="mt-8 max-w-xl">
            <p className="text-lg md:text-xl text-charcoal/75 leading-relaxed">
              Search homes, explore Georgia communities, estimate your buying power, or discover what your home could sell for across Metro Atlanta.
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/search" data-testid="hero-find-home" className="bg-gold text-navy font-semibold px-8 py-4 rounded-full hover:scale-105 transition-transform inline-flex items-center gap-2">
              Find a Home <ArrowUpRight size={18} />
            </Link>
            <Link to="/sell" data-testid="hero-home-worth" className="bg-navy text-cream font-semibold px-8 py-4 rounded-full hover:bg-navy/90 transition-colors">
              What Is My Home Worth?
            </Link>
            <Link to="/contact" data-testid="hero-talk-richard" className="text-charcoal font-medium underline underline-offset-4 hover:text-gold transition-colors">
              Talk With Richard
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-black/10 py-5 bg-cream">
        <Marquee speed={30} gradient={false}>
          {["Buyer Consultations", "Home-Value Reviews", "New Construction", "Relocation Guidance", "First-Time Buyers", "Investment Properties", "Showing Requests"].map((t, i) => (
            <span key={i} className="font-serif text-2xl md:text-3xl text-charcoal/70 mx-8">{t} <span className="text-gold mx-4">•</span></span>
          ))}
        </Marquee>
      </div>

      {/* JOURNEY SELECTOR */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-3">What are you planning to do?</h2></Reveal>
          <Reveal delay={0.1}><p className="text-charcoal/70 mb-12 max-w-lg">Pick a starting point and we'll guide you to the right experience.</p></Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {JOURNEYS.map((j) => (
              <motion.div key={j} variants={item}>
                <Link to={journeyLink(j)} data-testid={`journey-${j.toLowerCase().replace(/\s/g, "-")}`}
                  className="group flex items-center justify-between bg-white rounded-2xl p-7 border border-black/5 shadow-[0_8px_40px_rgba(0,0,0,0.03)] hover:-translate-y-1 transition-transform">
                  <span className="font-medium text-lg text-navy">{j}</span>
                  <ArrowUpRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* COUNTIES */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal><p className="font-mono text-xs uppercase tracking-[0.3em] text-green mb-3">01 — The Areas We Serve</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-12">Four counties. Endless possibilities.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {COUNTIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <div className="group relative rounded-[2rem] overflow-hidden h-[380px]">
                  <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
                  <div className="absolute bottom-0 p-8 text-cream">
                    <h3 className="font-serif text-3xl mb-2">{c.name}</h3>
                    <p className="text-sm text-cream/80 max-w-md mb-3">{c.desc}</p>
                    <p className="font-mono text-xs text-gold mb-4">{c.featured.slice(0, 4).join(" · ")}</p>
                    <Link to={`/areas/${c.slug}`} data-testid={`county-explore-${c.slug}`} className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-6 py-3 rounded-full text-sm hover:scale-105 transition-transform">
                      Explore This County <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal><p className="font-mono text-xs uppercase tracking-[0.3em] text-green mb-3">02 — Tools That Help</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-12">Plan your move with confidence.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TOOLS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.07}>
                <Link to={t.to} data-testid={`tool-${i}`} className="group block bg-white rounded-2xl p-7 border border-black/5 shadow-[0_8px_40px_rgba(0,0,0,0.03)] hover:-translate-y-1 transition-transform h-full">
                  <t.icon className="text-gold mb-6" size={30} />
                  <h3 className="font-serif text-xl text-navy mb-2">{t.title}</h3>
                  <p className="text-sm text-charcoal/65">{t.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SEARCHES */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-3">Featured searches</h2></Reveal>
          <Reveal delay={0.1}><p className="text-charcoal/70 mb-10 max-w-lg">Sample searches using placeholder listings. An approved MLS/IDX feed can be connected later.</p></Reveal>
          <div className="flex flex-wrap gap-3">
            {FEATURED_SEARCHES.map((s) => (
              <Link key={s.label} to={`/search${s.query}`} data-testid={`featured-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="border border-navy/15 rounded-full px-5 py-3 text-sm font-medium text-navy hover:bg-navy hover:text-cream transition-colors">
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-navy text-cream relative overflow-hidden">
        <img src={IMAGES.skyline1} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <img src={AGENT.headshot} alt={AGENT.name} className="rounded-[2rem] w-full max-w-md object-cover shadow-2xl" />
          </Reveal>
          <div>
            <Reveal><p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">03 — Your Local Guide</p></Reveal>
            <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl mb-6">Meet Richard Price</h2></Reveal>
            <Reveal delay={0.2}>
              <p className="text-cream/80 text-lg leading-relaxed mb-4">
                Richard Price is a licensed real estate professional serving buyers and sellers across Metro Atlanta. His approach is educational, no-pressure, and rooted in local market knowledge — helping you make informed decisions at every step.
              </p>
              <p className="font-mono text-sm text-cream/60 mb-8">{AGENT.brokerage} · License #{AGENT.license}</p>
            </Reveal>
            <Reveal delay={0.3} className="flex flex-wrap gap-4">
              <LeadDialog type="consultation" source="home-trust" title="Schedule a Consultation"
                successMsg="Thank you. Richard will reach out to confirm your consultation."
                trigger={<Button data-testid="trust-schedule" className="bg-gold text-navy font-semibold rounded-full px-7 h-12 hover:bg-gold/90">Schedule a Consultation</Button>} />
              <a href={AGENT.phoneHref} data-testid="trust-call" className="border border-white/25 rounded-full px-7 h-12 inline-flex items-center gap-2 hover:bg-white/10 transition-colors">
                <Phone size={16} /> Call Richard
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight mb-16">How it works</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-10">
            {[["Tell us what you need", "Share your goals through a quick, guided questionnaire."],
              ["Receive personalized guidance", "Get recommended next steps, areas, and resources."],
              ["Make your move with confidence", "Work with Richard to buy or sell on your terms."]].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <div className="border-t-2 border-gold pt-6">
                  <div className="font-serif text-5xl text-navy/15 mb-4">0{i + 1}</div>
                  <h3 className="font-serif text-2xl text-navy mb-2">{t}</h3>
                  <p className="text-charcoal/70">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <Reveal className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-6xl text-navy tracking-tight mb-8">Ready to start your next move?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/search" data-testid="final-search" className="bg-gold text-navy font-semibold px-8 py-4 rounded-full hover:scale-105 transition-transform">Start My Home Search</Link>
            <Link to="/sell" data-testid="final-value" className="bg-navy text-cream font-semibold px-8 py-4 rounded-full hover:bg-navy/90 transition-colors">Request My Home Value</Link>
            <Link to="/contact" data-testid="final-consult" className="border border-navy/20 text-navy font-semibold px-8 py-4 rounded-full hover:bg-navy hover:text-cream transition-colors">Schedule a Consultation</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
